import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@store'
import toast from 'react-hot-toast'
import apiClient from '@services/api/client'
import publicApiClient from '@services/api/publicClient'
import { storageURL, mediaUrl } from '@config'

function ProfilePage() {
  const { user, setUser } = useAuthStore()
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [isFreeTrialActive, setIsFreeTrialActive] = useState(false)
  const [freeTrialDaysLeft, setFreeTrialDaysLeft] = useState(0)
  const [createdAt, setCreatedAt] = useState(null)
  const [photoError, setPhotoError] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    bio: '',
    photo: ''
  })

  // Charger les données du profil
  useEffect(() => {
    if (user) {
      setFormData({
        name: [user.nom, user.prenom].filter(Boolean).join(' ') || user.name || '',
        email: user.email || '',
        phone: user.numero || user.phone || '',
        location: user.adresse || user.ville || user.location || '',
        website: user.siteWeb || user.website || '',
        bio: user.bio || '',
        photo: (() => {
          const p = user.photo_profil || user.photo || ''
          if (!p) return ''
          if (p.startsWith('http')) return p
          const base = storageURL.replace(/\/storage$/, '')
          if (p.startsWith('/storage/')) return `${base}${p}`
          return mediaUrl(p)
        })()
      })
      // Initialiser la date depuis le compte utilisateur
      if (user.created_at) {
        setCreatedAt(user.created_at)
      }
      checkFreeTrial()
    }
  }, [user])

  const checkFreeTrial = () => {
    // Source de vérité : date_fin_periode_gratuit (fixée à l'inscription : +30 jours)
    let endDate = user?.date_fin_periode_gratuit ? new Date(user.date_fin_periode_gratuit) : null

    // Repli si le champ est absent : created_at + 30 jours
    if (!endDate || isNaN(endDate.getTime())) {
      const createdDate = user?.created_at ? new Date(user.created_at) : null
      if (!createdDate) return
      endDate = new Date(createdDate.getTime() + 30 * 24 * 60 * 60 * 1000)
    }

    const now = new Date()
    const diffMs = endDate - now
    const daysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))

    setIsFreeTrialActive(diffMs > 0)
    setFreeTrialDaysLeft(daysLeft)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image')
      return
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image est trop volumineuse (max 5MB)')
      return
    }

    setUploadingPhoto(true)

    try {
      const clientId = user?.id || user?.client_id
      if (!clientId) {
        toast.error('Erreur: ID utilisateur non trouvé')
        return
      }

      const uploadFormData = new FormData()
      uploadFormData.append('photo', file)
      uploadFormData.append('client_id', clientId)

      const data = await apiClient.post('/client/photo', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      if (data.success) {
        // Construire l'URL complète en évitant le double /storage/
        const baseUrl = storageURL.replace(/\/storage$/, '')
        const rawUrl  = data.photo_url || ''
        const fullUrl = rawUrl.startsWith('http')
          ? rawUrl
          : rawUrl.startsWith('/storage/')
            ? `${baseUrl}${rawUrl}`
            : mediaUrl(rawUrl)
        setFormData(prev => ({ ...prev, photo: fullUrl }))
        setUser({ ...user, photo_profil: fullUrl, photo: fullUrl })
        toast.success('Photo de profil mise à jour !')
      } else {
        toast.error(data.message || 'Erreur lors de l\'upload')
      }
    } catch (error) {
      console.error('Erreur:', error)
      toast.error('Erreur lors de l\'upload de la photo')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const clientId = user?.id || user?.client_id
      
      if (!clientId) {
        toast.error('Erreur: ID utilisateur non trouvé')
        setLoading(false)
        return
      }

      const nameParts = formData.name.trim().split(' ')
      const data = await apiClient.put('/client/profile', {
        nom:     nameParts[0] || '',
        prenom:  nameParts.slice(1).join(' ') || '',
        email:   formData.email || null,
        numero:  formData.phone || null,
        adresse: formData.location || null,
        ville:   formData.location || null,
        bio:     formData.bio || null,
      })

      if (data.success) {
        const updatedUserData = {
          ...user,
          nom: data.data?.nom || data.data?.name || user.nom,
          prenom: data.data?.prenom || user.prenom,
          email: data.data?.email || formData.email,
          numero: data.data?.numero || formData.phone,
          adresse: data.data?.adresse || formData.location,
          siteWeb: data.data?.siteWeb || formData.website,
          bio: data.data?.bio || formData.bio,
        }
        setUser(updatedUserData)
        toast.success('Profil mis à jour avec succès !')
        setIsEditing(false)
      } else {
        toast.error(data.message || 'Erreur lors de la mise à jour')
      }
    } catch (error) {
      const status = error?.response?.status
      const msg = error?.response?.data?.message
        || (error?.response?.data?.errors ? JSON.stringify(error.response.data.errors) : null)
        || error?.message || 'Erreur de connexion'
      console.error('Update error', status, msg)
      toast.error(`[${status || '?'}] ${msg}`)
    } finally {
      setLoading(false)
    }
  }

  const calculateProfileCompletion = () => {
    const fields = [formData.name, formData.email, formData.phone, formData.location, formData.website, formData.bio, formData.photo]
    const filledFields = fields.filter(field => field && field.trim() !== '').length
    return Math.round((filledFields / fields.length) * 100)
  }

  return (
    <div className="home-font min-h-screen bg-slate-50 py-8">
      <div className="w-[94%] md:w-[80%] mx-auto">
        <div className="mb-8">
          <p className="text-sm font-bold uppercase tracking-widest text-[#B8860B]">Mon compte</p>
          <h1 className="mt-1 mb-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#002A4E]">Mon profil</h1>
          <p className="text-gray-600">Gérez vos informations personnelles et votre présence sur la plateforme</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <div className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70 text-center">
              <div className="relative w-32 h-32 mx-auto mb-4">
                {formData.photo && !photoError ? (
                  <img
                    src={formData.photo}
                    alt=""
                    className="w-full h-full rounded-full object-cover shadow-xl"
                    onError={() => setPhotoError(true)}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-xl select-none">
                    {(user?.nom?.charAt(0) || user?.name?.charAt(0) || 'U').toUpperCase()}
                    {(user?.prenom?.charAt(0) || '').toUpperCase()}
                  </div>
                )}
                <input 
                  type="file" 
                  id="photo-upload" 
                  accept="image/*" 
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <label 
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors border-2 border-[#D4AF37] cursor-pointer"
                >
                  {uploadingPhoto ? (
                    <svg className="animate-spin w-5 h-5 text-[#F5B700]" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </label>
              </div>
              <h3 className="text-2xl font-bold text-[#002A4E] mb-1">{formData.name || 'Utilisateur'}</h3>
              <p className="text-sm text-gray-500 mb-2">{formData.email}</p>
              {formData.location && (
                <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mb-4">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {formData.location}
                </div>
              )}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">Membre depuis</div>
                <div className="font-semibold text-[#002A4E]">
                  {createdAt 
                    ? new Date(createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                    : 'Chargement...'
                  }
                </div>
              </div>
            </div>

            {/* Période gratuite */}
            <div className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <h3 className="text-lg font-semibold text-[#002A4E] mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Période gratuite
              </h3>
              
              <div className="space-y-3">
                <div className={`p-3 rounded-md border ${isFreeTrialActive ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${isFreeTrialActive ? 'text-green-800' : 'text-red-800'}`}>Statut</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${isFreeTrialActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        {isFreeTrialActive
                          ? <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          : <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        }
                      </svg>
                      {isFreeTrialActive ? 'Actif' : 'Expiré'}
                    </span>
                  </div>
                  <div className="text-center py-2">
                    <div className={`text-3xl font-bold ${isFreeTrialActive ? 'text-green-600' : 'text-red-600'}`}>{freeTrialDaysLeft}</div>
                    <div className={`text-xs mt-1 ${isFreeTrialActive ? 'text-green-700' : 'text-red-700'}`}>jours restants</div>
                  </div>
                </div>

                {isFreeTrialActive && (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-xs">
                      <svg className="w-4 h-4 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">Publications illimitées</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <svg className="w-4 h-4 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">Support prioritaire</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <svg className="w-4 h-4 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700">Statistiques avancées</span>
                    </div>
                  </div>
                )}

                {!isFreeTrialActive && (
                  <div className="p-3 bg-slate-50 rounded-md border border-gray-200 text-center">
                    <p className="text-xs text-gray-600">Période gratuite expirée</p>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <h3 className="text-lg font-semibold text-[#002A4E] mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Profil complet
              </h3>
              <div className="mb-3">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 font-medium">Progression</span>
                  <span className="text-[#F5B700] font-bold text-lg">{calculateProfileCompletion()}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-[#D4AF37] to-[#c09d2a] h-3 rounded-full transition-all duration-500 shadow-inner" 
                    style={{ width: `${calculateProfileCompletion()}%` }}
                  ></div>
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.name ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.name ? 'text-gray-700' : 'text-gray-400'}>Nom complet</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.email ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.email ? 'text-gray-700' : 'text-gray-400'}>Email</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.phone ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.phone ? 'text-gray-700' : 'text-gray-400'}>Téléphone</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.location ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.location ? 'text-gray-700' : 'text-gray-400'}>Localisation</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.bio ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.bio ? 'text-gray-700' : 'text-gray-400'}>Bio</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.photo ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.photo ? 'text-gray-700' : 'text-gray-400'}>Photo de profil</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <svg className={`w-4 h-4 ${formData.website ? 'text-green-500' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className={formData.website ? 'text-gray-700' : 'text-gray-400'}>Site web</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 p-3 bg-blue-50 rounded-md border border-blue-100">
                💡 Un profil complet améliore votre visibilité et inspire confiance
              </p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <form onSubmit={handleSubmit} className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-[#002A4E] flex items-center gap-2">
                    <svg className="w-6 h-6 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Informations personnelles
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">Gérez vos informations de contact et votre présentation</p>
                </div>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-colors flex items-center gap-2 shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Modifier
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Nom complet
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 transition-all"
                      placeholder="Ex: Kouamé N'Goran"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-slate-50 rounded-md border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.name || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 transition-all"
                      placeholder="kouame@yahoo.fr"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-slate-50 rounded-md border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.email || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Téléphone
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 transition-all"
                      placeholder="+225 07 00 00 00 00"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-slate-50 rounded-md border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.phone || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Localisation
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 transition-all"
                      placeholder="Abidjan, Cocody Angré"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-slate-50 rounded-md border border-gray-200">
                      <p className="text-gray-900 font-medium">{formData.location || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Site web
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 transition-all"
                      placeholder="https://votreboutique.ci"
                    />
                  ) : (
                    <div className="px-4 py-3 bg-slate-50 rounded-md border border-gray-200">
                      <p className="text-gray-900 font-medium">
                        {formData.website ? (
                          <a href={formData.website} target="_blank" rel="noopener noreferrer" className="text-[#F5B700] hover:underline flex items-center gap-1">
                            {formData.website}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Non renseigné</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false)
                      if (user) {
                        setFormData(prev => ({
                          ...prev,
                          name: [user.nom, user.prenom].filter(Boolean).join(' ') || user.name || '',
                          email: user.email || '',
                          phone: user.numero || user.phone || '',
                          location: user.adresse || user.ville || user.location || '',
                          website: user.siteWeb || user.website || '',
                          bio: user.bio || '',
                        }))
                      }
                    }}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-[#FFD700] text-[#002A4E] font-bold rounded-md hover:bg-[#F5C800] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Enregistrer les modifications
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>

            <div className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <h2 className="text-2xl font-bold text-[#002A4E] mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-[#F5B700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                Bio
              </h2>
              {isEditing ? (
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-300 transition-all"
                  placeholder="Ex: Commerçant à Adjamé, je vends des pagnes wax et tissus depuis 10 ans..."
                />
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-md border border-gray-200 min-h-[120px]">
                  <p className="text-gray-700 whitespace-pre-wrap">{formData.bio || <span className="text-gray-400 italic">Aucune description disponible. Ajoutez une bio pour que les autres utilisateurs puissent mieux vous connaître.</span>}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
